import React from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle } from 'lucide-react';
const testimonials = [{
  text: 'Llegué al Día 1 sin esperanza. En el Día 12 volví a sentir que Dios me sostenía.',
  author: 'María T.',
  role: 'México',
  date: 'Feb 14, 2026',
  avatar: 'MT',
  rating: 5,
  verified: true,
  highlight: 'Día 12 — volví a sentir que Dios me sostenía'
}, {
  text: 'Estos 5 minutos cada mañana fueron lo único que me mantuvo en pie durante los peores meses de mi vida.',
  author: 'Roberto G.',
  role: 'Colombia',
  date: 'Ene 28, 2026',
  avatar: 'RG',
  rating: 5,
  verified: true,
  highlight: '5 minutos que me mantuvieron en pie'
}, {
  text: 'No es autoayuda con versículos. Es una conversación honesta con alguien que entendió exactamente lo que estaba viviendo.',
  author: 'Carmen L.',
  role: 'España',
  date: 'Feb 3, 2026',
  avatar: 'CL',
  rating: 5,
  verified: true,
  highlight: 'No es autoayuda — es honestidad real'
}, {
  text: 'Me dio permiso para estar triste y aun así buscar a Dios. Fue como un abrazo en la oscuridad.',
  author: 'Ana María G.',
  role: 'Argentina',
  date: 'Ene 15, 2026',
  avatar: 'AG',
  rating: 5,
  verified: true,
  highlight: 'Permiso para estar triste y aun así buscar a Dios'
}, {
  text: 'Al principio dudé porque ya tengo muchos libros sin terminar. Pero este es diferente: son solo 5 minutos y no te pide ser perfecto. Lo terminé completo por primera vez.',
  author: 'Diego F.',
  role: 'Perú',
  date: 'Feb 20, 2026',
  avatar: 'DF',
  rating: 4,
  verified: true,
  highlight: 'Lo terminé completo por primera vez'
}, {
  text: 'Perdí a mi mamá en diciembre. No podía orar, no podía dormir. El Día 5 fue el primero que dormí sin pastilla. No digo que sea magia, pero algo cambió.',
  author: 'Lucía M.',
  role: 'Chile',
  date: 'Feb 11, 2026',
  avatar: 'LM',
  rating: 5,
  verified: true,
  highlight: 'El Día 5 dormí sin pastilla por primera vez'
}];
function StarRating({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex items-center gap-0.5" data-id="element-404">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < count ? 'fill-[#00b67a] text-[#00b67a]' : 'text-gray-300'}`} data-id="element-405" />)}
    </div>;
}
function AvatarCircle({
  initials
}: {
  initials: string;
}) {
  const colors: Record<string, string> = {
    MT: '#7c6b3a',
    RG: '#5a7c6b',
    CL: '#6b5a7c',
    AG: '#7c5a5a',
    CR: '#5a6b7c',
    LM: '#7c7a5a'
  };
  const bg = colors[initials] || '#7c6b3a';
  return <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{
    backgroundColor: bg
  }} data-id="element-406">
      {initials}
    </div>;
}
export function TestimonialsSection() {
  const avgRating = 4.9;
  const totalReviews = 847;
  return <section className="py-8 md:py-14 px-4 md:px-6" style={{
    backgroundColor: 'var(--bg-deep)'
  }} data-id="element-407">
      <div className="max-w-[860px] mx-auto" data-id="element-408">
        {/* Header — Trustpilot style */}
        <div className="text-center mb-6 md:mb-10" data-id="element-409">
          <p className="text-[var(--accent)] text-xs font-bold tracking-widest uppercase mb-2" data-id="element-410">
            Reseñas Verificadas
          </p>
          <h2 className="text-xl md:text-3xl font-serif text-[var(--text-heading)] mb-4 md:mb-6" data-id="element-411">
            Lo que dicen quienes ya cruzaron el desierto
          </h2>

          {/* Aggregate score */}
          <div className="inline-flex items-center gap-3 md:gap-6 bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-xl px-4 md:px-6 py-3 md:py-4 shadow-sm" data-id="element-412">
            <div className="text-center" data-id="element-413">
              <div className="text-3xl md:text-4xl font-bold text-[var(--text-heading)] font-serif leading-none mb-1" data-id="element-414">
                {avgRating}
              </div>
              <StarRating count={5} data-id="element-415" />
            </div>
            <div className="w-px h-10 bg-[var(--border-subtle)]" data-id="element-416" />
            <div className="text-center" data-id="element-417">
              <div className="text-lg md:text-xl font-bold text-[var(--text-heading)]" data-id="element-418">
                {totalReviews.toLocaleString()}+
              </div>
              <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide" data-id="element-419">
                Lectores
              </p>
            </div>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4" data-id="element-420">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.07
        }} className="bg-[var(--bg-card)] rounded-xl border border-[var(--border-subtle)] p-3.5 md:p-5 flex flex-col gap-2 md:gap-3 shadow-sm" data-id="element-421">
              {/* Top row */}
              <div className="flex items-start justify-between gap-2" data-id="element-422">
                <div className="flex items-center gap-2" data-id="element-423">
                  <AvatarCircle initials={t.avatar} data-id="element-424" />
                  <div data-id="element-425">
                    <p className="text-[var(--text-heading)] text-xs md:text-sm font-bold leading-tight" data-id="element-426">
                      {t.author}
                    </p>
                    <p className="text-[var(--text-muted)] text-[10px]" data-id="element-427">
                      {t.role}
                    </p>
                  </div>
                </div>
                {t.verified && <div className="flex items-center gap-1 text-[10px] text-[#00b67a] flex-shrink-0" data-id="element-428">
                    <CheckCircle className="w-3 h-3" data-id="element-429" />
                  </div>}
              </div>

              {/* Highlight badge */}
              <div className="bg-[var(--accent-soft)] border border-[var(--accent)]/20 rounded px-2 py-1" data-id="element-430">
                <p className="text-[var(--accent)] text-[10px] md:text-[11px] font-semibold leading-snug" data-id="element-431">
                  "{t.highlight}"
                </p>
              </div>

              {/* Full review */}
              <p className="text-[var(--text-muted)] text-xs md:text-sm leading-relaxed flex-1 italic" data-id="element-432">
                "{t.text}"
              </p>
            </motion.div>)}
        </div>

        {/* Footer trust line */}
        <div className="mt-5 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-2 text-[10px] text-[var(--text-muted)]" data-id="element-433">
          <div className="flex items-center gap-1.5" data-id="element-434">
            <CheckCircle className="w-3.5 h-3.5 text-[#00b67a]" data-id="element-435" />
            <span data-id="element-436">Reseñas verificadas</span>
          </div>
          <span className="hidden sm:inline opacity-30" data-id="element-437">·</span>
          <span data-id="element-438">4.9/5 · +847 personas ya comenzaron</span>
        </div>
      </div>
    </section>;
}